import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import Table from '../common/Table';
import Tabs from '../common/Tabs';
import DebtList from '../debts/DebtList';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import '../../styles/subscription.css';

const FinanceHub = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    category: 'General',
    description: '',
    amount: '',
    expense_date: new Date().toISOString().slice(0, 10),
    payment_method: 'cash'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadFinance();
  }, []);

  const loadFinance = async () => {
    setLoading(true);
    const empty = {
      sales_total: 0,
      total_expenses: 0,
      net_estimate: 0,
      outstanding_debt: 0,
      active_debts: 0
    };
    try {
      const [sumRes, expRes] = await Promise.all([
        api.get('/expenses.php/summary'),
        api.get('/expenses.php')
      ]);
      setSummary({ ...empty, ...(sumRes.data || {}) });
      setExpenses(expRes.data.expenses || []);
    } catch (err) {
      console.error(err);
      setSummary(empty);
      setExpenses([]);
      toast.error(err.response?.data?.error || 'Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  const saveExpense = async (e) => {
    e.preventDefault();
    if (!form.amount) {
      toast.error('Amount required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/expenses.php', form);
      toast.success('Expense recorded');
      setForm((f) => ({ ...f, description: '', amount: '' }));
      loadFinance();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await api.delete(`/expenses.php/${id}`);
      loadFinance();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const expenseColumns = [
    { key: 'expense_date', label: 'Date', render: (v) => formatDate(v) },
    { key: 'category', label: 'Category' },
    { key: 'description', label: 'Description' },
    { key: 'amount', label: 'Amount', render: (v) => formatCurrency(v) },
    { key: 'payment_method', label: 'Method' },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <Button size="sm" variant="danger" onClick={() => deleteExpense(row.id)}>
          Delete
        </Button>
      )
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'debts', label: 'Debts / AR' },
    { id: 'expenses', label: 'Expenses' }
  ];

  return (
    <div className="finance-hub">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1>Finance</h1>
          <p>Debts, expenses, and cash overview for your business</p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/reports')}>
          Financial reports
        </Button>
      </div>

      <Tabs tabs={tabs} activeTab={tab} onTabChange={setTab} />

      {tab === 'overview' && (
        <div style={{ marginTop: '1rem' }}>
          {loading ? (
            <p>Loading…</p>
          ) : (
            <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '1rem' }}>
              <Card>
                <h4>Sales (period)</h4>
                <strong style={{ fontSize: '1.4rem' }}>{formatCurrency(summary?.sales_total)}</strong>
              </Card>
              <Card>
                <h4>Expenses</h4>
                <strong style={{ fontSize: '1.4rem' }}>{formatCurrency(summary?.total_expenses)}</strong>
              </Card>
              <Card>
                <h4>Net estimate</h4>
                <strong style={{ fontSize: '1.4rem' }}>{formatCurrency(summary?.net_estimate)}</strong>
              </Card>
              <Card>
                <h4>Outstanding debt</h4>
                <strong style={{ fontSize: '1.4rem' }}>{formatCurrency(summary?.outstanding_debt)}</strong>
                <p style={{ margin: '0.35rem 0 0', color: '#64748b' }}>{summary?.active_debts || 0} active</p>
              </Card>
            </div>
          )}
          <div style={{ marginTop: '1.25rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <Button variant="primary" onClick={() => setTab('debts')}>Manage debts</Button>
            <Button variant="secondary" onClick={() => setTab('expenses')}>Add expense</Button>
            <Link to="/customers?debtors=1"><Button variant="secondary">View debtors (CRM)</Button></Link>
          </div>
        </div>
      )}

      {tab === 'debts' && (
        <div style={{ marginTop: '1rem' }}>
          <DebtList />
        </div>
      )}

      {tab === 'expenses' && (
        <div style={{ marginTop: '1rem', display: 'grid', gap: '1rem', gridTemplateColumns: 'minmax(260px,360px) 1fr' }}>
          <Card title="Record expense">
            <form onSubmit={saveExpense}>
              <Input
                label="Category"
                name="category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
              <Input
                label="Amount"
                name="amount"
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                required
              />
              <Input
                label="Date"
                name="expense_date"
                type="date"
                value={form.expense_date}
                onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                required
              />
              <Input
                type="textarea"
                label="Description"
                name="description"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
              <Input
                type="select"
                label="Payment method"
                name="payment_method"
                value={form.payment_method}
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
              >
                <option value="cash">Cash</option>
                <option value="mpesa">M-Pesa</option>
                <option value="bank">Bank</option>
                <option value="card">Card</option>
              </Input>
              <Button type="submit" variant="primary" loading={saving} fullWidth>
                Save expense
              </Button>
            </form>
          </Card>
          <Card title="Recent expenses">
            <Table columns={expenseColumns} data={expenses} loading={loading} />
          </Card>
        </div>
      )}
    </div>
  );
};

export default FinanceHub;
